import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { snakeCase } from 'lodash';

import { ButtonModel, RadioButton, Spinner } from '@notino/react-styleguide';
import {
  GetCatalogProductViewQuery,
  StockAvailability,
} from '@notino/shared/definitions/types';
import { CartType } from '@notino/web-tracking';

import { useModifaceVariants } from '@containers/ProductDetailContainer/ProductDetail/hooks/useModifaceVariants';
import { usePriceLogic } from '@containers/ProductDetailContainer/ProductDetail/hooks/usePriceLogic';
import {
  MIN_PRICE_PROMO_LABEL,
  RRP_PRICE_PROMO_LABEL,
} from '@containers/ProductDetailContainer/ProductDetail/hooks/usePriceLogic/tracking';
import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetail/ProductDetailContext';
import { dispatchTrackingEvent } from '@context/tracking/utils';
import { ProductEventWither } from '@helpers/googleTagManager';

import { Store } from './components/Store';
import { useTrackModalShow } from './hooks/useTrackModalShow';
import { messages } from './messages';
import {
  ModalContentWrapper,
  StoreWrapper,
  CenterWrapper,
  StyledActionButtonWithConfirmation,
} from './styled';
import { useClickAndCollect } from './useClickAndCollect';

interface IClickAndCollectModalProps {
  product: GetCatalogProductViewQuery['productDetailByCatalogMasterId'];
  selectedVariant: GetCatalogProductViewQuery['productDetailByCatalogMasterId']['variants'][number];
}

export const ClickAndCollectModal = ({
  selectedVariant,
  product,
}: IClickAndCollectModalProps) => {
  const [selectedStore, setSelectedStore] = React.useState('');
  const { rrpShown, minimalPriceShown } = usePriceLogic(selectedVariant);

  const {
    tryItFirstAvailable,
    engravingAvailable,
    product: { variants },
  } = useProductDetailContext();
  const modifaceVariants = useModifaceVariants(variants);

  const {
    stores,
    storesLoading,
    handleAddToClickAndCollect,
    addToClickAndCollectData: {
      loading: addToCnCLoading,
      error: addToCnCError,
    },
  } = useClickAndCollect(Number(selectedVariant.webId));

  useTrackModalShow();

  if (storesLoading) {
    return (
      <CenterWrapper tabIndex={0}>
        <Spinner color="icon.primary" size={32} />
      </CenterWrapper>
    );
  }

  const onRadioButtonClick = (storeId: string) => () => {
    setSelectedStore(storeId);
  };

  const handleSubmit = async () => {
    dispatchTrackingEvent({
      event: 'add_to_cart',
      product: ProductEventWither()
        .withProduct(product)
        .withVariant(selectedVariant)
        .withServices({
          modifaceVariants,
          tryItFirstAvailable,
          engravingAvailable,
          shadefinderAvailable: !!product.features.isShadeFinderAvailable,
        })
        .withAdditionalData({
          quantity: 1,
          cart_type: snakeCase(CartType.clickAndCollect),
        })
        .withAdditionalPromoLabels([
          rrpShown && RRP_PRICE_PROMO_LABEL,
          minimalPriceShown && MIN_PRICE_PROMO_LABEL,
        ])
        .build(),
      _clear: true,
    });

    await handleAddToClickAndCollect(selectedStore);
  };

  return (
    <>
      <ModalContentWrapper>
        {stores?.collectStoresByProductId?.map((store) => {
          const outOfStock = store.stockCount === StockAvailability.OutOfStock;
          if (outOfStock) {
            return null;
          }

          return (
            <StoreWrapper key={store.code}>
              <RadioButton
                onChange={onRadioButtonClick(store.code)}
                position={4}
                disabled={false}
                checked={selectedStore === store.code}
              >
                <Store data={store} />
              </RadioButton>
            </StoreWrapper>
          );
        })}
      </ModalContentWrapper>

      <StyledActionButtonWithConfirmation
        buttonStyle={ButtonModel.Styles.primary}
        fullWidth={true}
        onClick={handleSubmit}
        loading={addToCnCLoading}
        error={!!addToCnCError}
        disabled={!selectedStore}
      >
        <FormattedMessage {...messages.pickHere} />
      </StyledActionButtonWithConfirmation>
    </>
  );
};
